import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText } from "lucide-react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { printPedidoA5, printNotaFactura, printFactura2, dotMatrixTxtOn, mergeVariantLines, lineVariant } from "@/utils/wholesaleInvoice";
import { getDocPrinter, setDocPrinter } from "@/utils/printerPrefs";
import { curSym } from "../../utils/cur";

const TABS = [
  { id: "pedido", label: "📋 Pedido", zh: "订货单" },
  { id: "nota", label: "🚚 Nota de Entrega", zh: "送货单" },
  { id: "factura", label: "🧾 Factura", zh: "发票" },
  { id: "factura2", label: "🖨️ Factura 2", zh: "发票2·激光", flag: "b2b_factura2_enabled" },
  { id: "nc", label: "💳 Nota de Crédito", zh: "贷记单" },
];
const FISCAL = ["factura", "factura2", "nc"];

export function B2BDocsDialog({ open, onOpenChange, order, settings, exchangeRates, storeInfo, printerName, initialTab }) {
  // Bs rate defaults — identical for wholesale page & online-orders page:
  //  Pedido/Nota → boss's own 销售汇率 (设置→汇率 sales_rates[0]) > rate locked on the order > BCV today
  //  Factura/NC (fiscal) → rate locked on the order > BCV today
  const salesRates = (exchangeRates?.sales_rates || []).filter(r => Number(r?.rate) > 0);
  const bcvToday = Number(exchangeRates?.sales_exchange_rate || exchangeRates?.usd_to_ves) || 0;
  // 🔒 Leyenda 2026-10-24: "Factura 要固定用我设置的销售汇率" — lock lives in the tenant's exchange_rates doc (fiscal_rate_lock {name, rate})
  // so it follows the account across devices; localStorage is only an offline cache.
  const tenantKey = (() => { try { const u = JSON.parse(sessionStorage.getItem("cached_auth_user") || localStorage.getItem("cached_auth_user") || "{}"); return u.tenant_id || u.tenant?.id || "x"; } catch { return "x"; } })();
  const LOCK_KEY = `b2b_fiscal_rate_lock_${tenantKey}`;
  const [fiscalLock, setFiscalLock] = useState(() => {
    if (exchangeRates && "fiscal_rate_lock" in exchangeRates) return exchangeRates.fiscal_rate_lock || null;
    try { return JSON.parse(localStorage.getItem(LOCK_KEY) || "null"); } catch { return null; }
  });
  useEffect(() => {
    // server wins over the device cache (another PC may have cleared the lock)
    if (exchangeRates && "fiscal_rate_lock" in exchangeRates) {
      const v = exchangeRates.fiscal_rate_lock || null; setFiscalLock(v);
      try { v ? localStorage.setItem(LOCK_KEY, JSON.stringify(v)) : localStorage.removeItem(LOCK_KEY); } catch { /* noop */ }
    }
  }, [exchangeRates?.fiscal_rate_lock]); // eslint-disable-line react-hooks/exhaustive-deps
  const lockIdx = fiscalLock ? (() => { const byName = fiscalLock.name ? salesRates.findIndex(r => (r.name || "") === fiscalLock.name) : -1; return byName >= 0 ? byName : salesRates.findIndex(r => Number(r.rate) === Number(fiscalLock.rate)); })() : -1;
  const lockedRate = lockIdx >= 0 ? Number(salesRates[lockIdx].rate) : (fiscalLock && Number(fiscalLock.rate) > 0 ? Number(fiscalLock.rate) : 0);
  const defRateFor = (t) => (FISCAL.includes(t) ? lockedRate : Number(salesRates[0]?.rate) || 0) || Number(order?.bcv_rate) || bcvToday;
  const setLock = (v) => {
    setFiscalLock(v);
    try { v ? localStorage.setItem(LOCK_KEY, JSON.stringify(v)) : localStorage.removeItem(LOCK_KEY); } catch { /* noop */ }
    axios.put(`${API}/exchange-rates/fiscal-lock`, v || {}).then(() => { if (exchangeRates) exchangeRates.fiscal_rate_lock = v; }).catch(() => toast.error("保存固定汇率失败 / No se pudo guardar"));
  };
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState(initialTab || "pedido");
  const [rate, setRate] = useState(defRateFor(initialTab || "pedido"));
  const [currency, setCurrency] = useState("VES");
  const [rmbRate, setRmbRate] = useState(0);
  const [preprinted, setPreprinted] = useState(() => localStorage.getItem("sellox_b2b_preprinted") !== "0");
  const [mergeSku, setMergeSku] = useState(() => localStorage.getItem("sellox_b2b_merge_sku") === "1");
  // vendedor-app lines only carry sku_label/sku_key (no `variant`) → use the same resolver the printer uses
  const canMerge = React.useMemo(() => { const c = {}; (order?.items || []).forEach(it => { if (lineVariant(it) && it.product_id) c[it.product_id] = (c[it.product_id] || 0) + 1; }); return Object.values(c).some(n => n > 1); }, [order]);
  const [facturaNo, setFacturaNo] = useState("");
  const [ncNo, setNcNo] = useState("");
  const [refFactura, setRefFactura] = useState("");
  const [printers, setPrinters] = useState([]);
  const [printer, setPrinter] = useState(() => getDocPrinter("pedido"));
  const [itemCats, setItemCats] = useState(null);   // product_id -> {category, box_quantity}
  const [catRates, setCatRates] = useState({});      // category name -> rate override
  const [showCatRates, setShowCatRates] = useState(false);
  // 每页货品行数（分页阈值）— 租户可在打印时临时调整（Leyenda 2026-10-18）
  const [rowsPerPage, setRowsPerPage] = useState(() => Math.max(5, Number(settings?.nf_rows_per_page) || 26));

  useEffect(() => {
    if (open && typeof window !== "undefined" && window.selloxDesktop?.listPrinters) {
      window.selloxDesktop.listPrinters().then(l => setPrinters(Array.isArray(l) ? l : [])).catch(() => {});
    }
    if (open) {
      // ¥ rate priority: rate locked on the order (RMB order exchange_rate / shop rmb_rate) > manual ¥ sales rate (设置→汇率) > live market
      const orderRmb = (order?.currency === "RMB" && Number(order?.exchange_rate) > 1) ? Number(order.exchange_rate) : (Number(order?.rmb_rate) > 0 ? Number(order.rmb_rate) : 0);
      const manual = Number(exchangeRates?.rmb_sales_rate) || 0;
      // Nota shows the ¥ columns whenever this shop works in RMB (order locked a ¥ rate, or the
      // boss configured a ¥ sales rate) — same as the online-orders page, so an online order
      // converted to a wholesale order prints exactly the same Nota.
      if (order?.currency === "RMB" || orderRmb > 0 || manual > 0) setCurrency("RMB");
      if (orderRmb || manual) setRmbRate(orderRmb || manual);
      else axios.get(`${API}/products/container/rmb-rate`).then(r => setRmbRate(Number(r.data?.rate) || 0)).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each doc type remembers its own printer (Pedido → térmica, Factura → matriz…)
  useEffect(() => { setPrinter(getDocPrinter(tab)); setRate(defRateFor(tab)); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) {
      const t0 = initialTab || "pedido";
      // a wholesale return (Devolución) hands us a synthetic order: returned lines only + reserved NC number
      setTab(t0); setRate(defRateFor(t0)); setFacturaNo(""); setNcNo(order?.nc_no || ""); setRefFactura(order?.ref_factura || "");
      setItemCats(null); setCatRates({}); setShowCatRates(false);
    }
  }, [open, order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load item catalog info (department, box qty, live unit) whenever the dialog opens
  useEffect(() => {
    if (!open || !order || itemCats) return;
    const pids = [...new Set((order.items || []).map(it => it.product_id).filter(Boolean))];
    if (!pids.length) { setItemCats({}); return; }
    axios.post(`${API}/b2b-docs/item-categories`, { product_ids: pids })
      .then(r => setItemCats(r.data || {}))
      .catch(() => setItemCats({}));
  }, [open, order, itemCats]);

  // Printed unit always follows the CURRENT catalog unit (tenant renamed PZ→BT → old docs reprint as BT)
  const withLiveUnits = (o, cats) => ({
    ...o,
    items: (o.items || []).map(it => {
      const c = cats?.[it.product_id];
      const live = (it.sku_key && c?.sku_units?.[it.sku_key]) || c?.unit;
      return live ? { ...it, unit: live } : it;
    }),
  });

  const categories = React.useMemo(() => {
    if (!itemCats) return [];
    return [...new Set(Object.values(itemCats).map(v => v.category || "General"))];
  }, [itemCats]);

  const doPrint = async () => {
    if (busy || !order) return;
    setBusy(true);
    try {
      let fn = tab === "nc" ? ncNo : facturaNo;
      if (FISCAL.includes(tab) && !fn) {
        const r = await axios.post(`${API}/b2b-docs/factura-number`, { order_no: order.order_no, rate: Number(rate) || 0, doc_type: tab === "nc" ? "nc" : "factura" });
        fn = r.data.factura_no;
        if (tab === "nc") setNcNo(fn); else setFacturaNo(fn);
      }
      const effPrinter = printer || printerName;
      const printOpts = effPrinter ? { printerName: effPrinter } : undefined;
      const cleanCatRates = {};
      Object.entries(catRates).forEach(([k, v]) => { if (Number(v) > 0) cleanCatRates[k] = Number(v); });
      const effSettings = { ...(settings || {}), company_name: settings?.company_name || storeInfo?.name || "" };
      let cats = itemCats;
      if (!cats) {
        const pids = [...new Set((order.items || []).map(it => it.product_id).filter(Boolean))];
        cats = pids.length ? await axios.post(`${API}/b2b-docs/item-categories`, { product_ids: pids }).then(r => r.data || {}).catch(() => ({})) : {};
        setItemCats(cats);
      }
      const ord = withLiveUnits(mergeSku && canMerge ? mergeVariantLines(order) : order, cats);
      if (tab === "pedido") {
        printPedidoA5(ord, effSettings, printOpts, { rate: Number(rate) || 0, currency, rmbRate: Number(rmbRate) || 0 });
      } else if (tab === "factura2") {
        await printFactura2(ord, effSettings, exchangeRates, { rate: Number(rate) || 0, currency: "VES", docNo: fn, refFactura, catRates: cleanCatRates, itemCats: cats || {}, rowsPerPage: Math.max(5, Number(rowsPerPage) || 0) || undefined }, printOpts);
      } else {
        printNotaFactura(ord, effSettings, exchangeRates, tab, tab === "nota" && currency === "RMB" ? (Number(rmbRate) || 0) : 0, {
          rate: Number(rate) || 0,
          currency: currency === "USD" ? "USD" : "VES",
          docNo: FISCAL.includes(tab) ? fn : undefined,
          preprinted,
          refFactura, catRates: cleanCatRates, itemCats: cats || {},
          rowsPerPage: Math.max(5, Number(rowsPerPage) || 0) || undefined,
        }, printOpts);
      }
      // 🖨️ 租户要求 2026-10-16：确认打印后自动关闭打印界面
      setTimeout(() => onOpenChange?.(false), 450);
    } catch {
      toast.error("Error al generar documento");
    }
    setBusy(false);
  };

  const numRate = Number(rate) || 0;
  const totalUsd = order?.total_amount || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white w-[96vw] max-w-md grid-cols-[minmax(0,1fr)] max-h-[92vh] overflow-y-auto" data-testid="b2b-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 min-w-0 pr-6">
            <FileText className="w-5 h-5 text-indigo-400 shrink-0" />
            <span className="truncate">📑 Documentos B2B — {order?.order_no}</span>
          </DialogTitle>
        </DialogHeader>
        {(
          <div className="space-y-3" data-testid="b2b-docs-panel">
            <div className="flex gap-1">
              {TABS.filter(t => !t.flag || settings?.[t.flag]).map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex-1 min-w-0 truncate text-xs py-2 px-1 rounded-lg border transition-colors ${tab === t.id ? "border-indigo-500 bg-indigo-600/30 text-white" : "border-slate-600 text-slate-400 hover:text-slate-200"}`}
                  data-testid={`b2b-tab-${t.id}`}>
                  {t.label}<span className="block text-[10px] opacity-70">{t.zh}</span>
                </button>
              ))}
            </div>
            {!FISCAL.includes(tab) && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-24">Moneda / 货币</span>
                <select value={currency} onChange={e => setCurrency(e.target.value)}
                  className="bg-slate-700 border border-slate-600 text-white rounded px-2 py-1.5 text-sm flex-1 min-w-0 w-full" data-testid="b2b-currency">
                  <option value="VES">Bs (Bolívares)</option>
                  <option value="USD">{curSym()} (USD)</option>
                  <option value="RMB">¥ RMB (人民币)</option>
                </select>
              </div>
            )}
            {!FISCAL.includes(tab) && currency === "RMB" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-24">{curSym()}1 = ¥</span>
                <Input type="number" min="0" step="0.0001" value={rmbRate}
                  onChange={e => setRmbRate(e.target.value)}
                  className="bg-slate-700 border-slate-600 h-8 text-sm flex-1" data-testid="b2b-rmb-rate-input" />
              </div>
            )}
            {(FISCAL.includes(tab) || currency !== "USD") && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-24">Tasa BCV / 汇率</span>
                  <Input type="number" min="0" step="0.0001" value={rate}
                    onChange={e => setRate(e.target.value)}
                    className="bg-slate-700 border-slate-600 h-8 text-sm flex-1" data-testid="b2b-rate-input" />
                </div>
                <div className="flex gap-1.5 flex-wrap pl-[104px]">
                  {salesRates.map((r, i) => (
                    <button key={i} onClick={() => { setRate(Number(r.rate)); if (FISCAL.includes(tab) && fiscalLock) setLock({ name: r.name || "", rate: Number(r.rate) }); }}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${Number(rate) === Number(r.rate) ? "bg-amber-500/40 border-amber-400 text-amber-100" : "bg-amber-600/20 border-amber-500/40 text-amber-300 hover:bg-amber-600/40"}`} data-testid={`b2b-rate-sales-${i}`}>
                      {lockIdx === i && FISCAL.includes(tab) ? "🔒 " : ""}{r.name || `销售汇率 ${i + 1}`} {Number(r.rate).toFixed(2)}
                    </button>
                  ))}
                  {bcvToday > 0 && (
                    <button onClick={() => setRate(bcvToday)}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-600/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-600/40 transition-colors" data-testid="b2b-rate-bcv-today">
                      BCV今日 {bcvToday.toFixed(2)}
                    </button>
                  )}
                  {FISCAL.includes(tab) && salesRates.length > 0 && (
                    <label className={`text-[11px] px-2 py-0.5 rounded-full border cursor-pointer select-none flex items-center gap-1 ${fiscalLock ? "bg-rose-600/30 border-rose-400 text-rose-100" : "bg-slate-700 border-slate-500 text-slate-300 hover:bg-slate-600"}`} title="固定：Factura/NC 默认用这个销售汇率，不再跳回 BCV / Fijar: la factura usa siempre esta tasa de venta" data-testid="b2b-rate-lock">
                      <input type="checkbox" className="accent-rose-400" checked={!!fiscalLock}
                        onChange={e => { if (e.target.checked) { const i = Math.max(0, salesRates.findIndex(r => Number(r.rate) === Number(rate))); setLock({ name: salesRates[i].name || "", rate: Number(salesRates[i].rate) }); setRate(Number(salesRates[i].rate)); } else { setLock(null); setRate(Number(order?.bcv_rate) || bcvToday); } }} />
                      {fiscalLock ? "🔒 已固定销售汇率 / Fijada" : "固定销售汇率 / Fijar tasa de venta"}
                    </label>
                  )}
                  {Number(order?.bcv_rate) > 0 && (
                    <button onClick={() => setRate(Number(order.bcv_rate))}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-sky-600/20 border border-sky-500/40 text-sky-300 hover:bg-sky-600/40 transition-colors" data-testid="b2b-rate-order">
                      下单汇率 {Number(order.bcv_rate).toFixed(2)}
                    </button>
                  )}
                </div>
              </div>
            )}
            {canMerge && (
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer" data-testid="b2b-merge-sku-label">
                <input type="checkbox" checked={mergeSku}
                  onChange={e => { setMergeSku(e.target.checked); localStorage.setItem("sellox_b2b_merge_sku", e.target.checked ? "1" : "0"); }}
                  className="w-4 h-4 accent-pink-500" data-testid="b2b-merge-sku" />
                <span>🎨 合并规格/颜色为一行（省纸） / Agrupar variantes en una línea</span>
              </label>
            )}
            {tab !== "pedido" && (
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <input type="checkbox" checked={preprinted}
                  onChange={e => { setPreprinted(e.target.checked); localStorage.setItem("sellox_b2b_preprinted", e.target.checked ? "1" : "0"); }}
                  className="w-4 h-4 accent-indigo-500" data-testid="b2b-preprinted" />
                <span>预印纸（纸已带店名{FISCAL.includes(tab) ? "和控制号" : ""}） / Papel preimpreso</span>
              </label>
            )}
            {tab !== "pedido" && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-24">每页行数 / Filas x pág</span>
                <Input type="number" min="5" max="60" step="1" value={rowsPerPage}
                  onChange={e => setRowsPerPage(e.target.value)}
                  className="bg-slate-700 border-slate-600 h-8 text-sm w-24" data-testid="b2b-rows-per-page" />
                <span className="text-[10px] text-slate-500 flex-1">超过此行数自动分页：每页重复客户表头 + TRANSPORTE/VIENE 自动累计金额 / Si hay más líneas, pagina solo: repite encabezado y acumula TRANSPORTE/VIENE</span>
              </div>
            )}
            {tab === "nota" && currency === "RMB" && (
              <div className="text-xs text-amber-300/80 bg-slate-700/40 rounded-lg px-3 py-2" data-testid="b2b-rmb-hint">
                {Number(rmbRate) > 0 ? `RMB汇率 ${rmbRate} — 送货单显示RMB列 + ¥/${curSym()}双币总额 / La nota incluye columnas RMB y totales ¥+${curSym()}` : "未配置RMB汇率，RMB列将留空 / Sin tasa RMB configurada"}
              </div>
            )}
            {(tab === "factura" || tab === "factura2") && (
              <div className="text-xs text-slate-400 bg-slate-700/40 rounded-lg px-3 py-2" data-testid="b2b-factura-info">
                <div>N° Factura: <span className="text-indigo-300 font-mono">{facturaNo || "自动分配 / se asigna al imprimir"}</span></div>
                <div className="mt-0.5">独立编号序列 · IVA明细 · 重印保持同号 / Numeración independiente, mismo N° al reimprimir</div>
              </div>
            )}
            {tab === "nc" && (
              <div className="space-y-2">
                <div className="text-xs text-slate-400 bg-slate-700/40 rounded-lg px-3 py-2" data-testid="b2b-nc-info">
                  <div>N° Nota de Crédito: <span className="text-rose-300 font-mono">{ncNo || "自动分配 / se asigna al imprimir"}</span></div>
                  <div className="mt-0.5">格式与Factura相同 · 独立NC编号 / Mismo formato que la factura, numeración NC propia</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-24">Fact. afectada</span>
                  <Input value={refFactura} onChange={e => setRefFactura(e.target.value)} placeholder="FAC-000123 (opcional)"
                    className="bg-slate-700 border-slate-600 h-8 text-sm flex-1" data-testid="b2b-ref-factura" />
                </div>
              </div>
            )}
            {(tab === "factura" || tab === "nc") && categories.length > 0 && (
              <div className="space-y-1.5">
                <button onClick={() => setShowCatRates(v => !v)}
                  className="text-xs text-amber-300 hover:text-amber-200 underline underline-offset-2" data-testid="b2b-cat-rates-toggle">
                  {showCatRates ? "▾" : "▸"} 按部门改汇率 / Tasa por departamento ({categories.length})
                </button>
                {showCatRates && (
                  <div className="space-y-1 bg-slate-700/30 rounded-lg p-2 max-h-40 overflow-y-auto" data-testid="b2b-cat-rates-panel">
                    <div className="text-[10px] text-slate-500">只影响这张打印单的Bs价格，不改系统 / Solo afecta los precios impresos, no cambia nada en el sistema</div>
                    {categories.map(cat => (
                      <div key={cat} className="flex items-center gap-2">
                        <span className="text-xs text-slate-300 flex-1 truncate" title={cat}>{cat}</span>
                        <Input type="number" min="0" step="0.0001" value={catRates[cat] ?? ""}
                          onChange={e => setCatRates(prev => ({ ...prev, [cat]: e.target.value }))}
                          placeholder={String(Number(rate) || "")}
                          className="bg-slate-700 border-slate-600 h-7 text-xs w-28" data-testid={`b2b-cat-rate-${cat}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {printers.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-24">打印机 / Impresora</span>
                <select value={printer}
                  onChange={e => { setPrinter(e.target.value); setDocPrinter(tab, e.target.value); }}
                  className="bg-slate-700 border border-slate-600 text-white rounded px-2 py-1.5 text-sm flex-1 min-w-0 w-full" data-testid="b2b-printer-select">
                  <option value="">🖨️ 默认 / Predeterminada</option>
                  {printers.map(p => <option key={p.name} value={p.name}>🖨️ {p.displayName || p.name}</option>)}
                </select>
              </div>
            )}
            {printers.length > 0 && (
              <p className="text-[10px] text-slate-500 -mt-1.5 pl-[104px]" data-testid="b2b-print-mode">
                每种单据记住各自的打印机 / Cada documento recuerda su impresora ·{" "}
                {dotMatrixTxtOn(printer || printerName, settings)
                  ? <span className="text-emerald-400">针式 TXT 直打 / ESC-P</span>
                  : <span className="text-sky-300">驱动打印 HTML{tab === "pedido" ? ` · 纸张 ${({ a5c: "A5居中于信纸", half: "½ carta", letter: "Carta", a4: "A4", auto: "Auto" })[settings?.pedido_paper] || "A5"}` : ""}</span>}
              </p>
            )}
            <div className="flex items-center justify-between text-sm bg-slate-700/40 rounded-lg px-3 py-2">
              <span className="text-slate-400">Total pedido:</span>
              <span className="text-emerald-400 font-bold" data-testid="b2b-total-preview">
                {curSym()}{totalUsd.toFixed(2)}{currency === "RMB" && Number(rmbRate) > 0 && <span className="text-rose-300 ml-2">¥ {(totalUsd * Number(rmbRate)).toFixed(2)}</span>}{numRate > 0 && <span className="text-amber-400 ml-2">Bs {Math.round(totalUsd * numRate).toLocaleString()}</span>}
              </span>
            </div>
            <Button onClick={doPrint} disabled={busy} className="w-full bg-indigo-600 hover:bg-indigo-700 gap-2" data-testid="b2b-print-btn">
              🖨️ {busy ? "..." : `Imprimir ${TABS.find(t => t.id === tab)?.label.slice(2).trim()} / 打印`}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
